import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Row, Col, Button, Table, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { JhiItemCount, JhiPagination } from 'react-jhipster';
import { ITopic } from 'app/shared/model/topic.model';
import IconActionButton from 'app/shared/common-fun/IconActionButton';
import { useParentTopics } from 'app/shared/common-fun/useParentTopics';

export const PAGE_SIZE = 20;
const NOTICE_MS = 3000;

interface Props {
  // parent picked with "View Sub Topics" in the Topics tab: its sub topics are listed first
  initialParentId?: number | null;
  // shows a "Back to Topics" button while the sub topics of one parent are listed
  onBackToTopics?: () => void;
}

/** Admin tab to manage sub topics. A sub topic is a topic with a parent; add and edit happen in a modal. */
const SubTopics: React.FC<Props> = ({ initialParentId, onBackToTopics }) => {
  const parentTopics = useParentTopics();
  const [subTopics, setSubTopics] = useState<ITopic[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [parentFilter, setParentFilter] = useState(initialParentId ? String(initialParentId) : '');
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ITopic | null>(null);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  // the parent is known when editing, or when adding from the list of one parent: it is then shown in the title only
  const [parentKnown, setParentKnown] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState<ITopic | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [notice, setNotice] = useState('');

  const modalParentName = parentTopics.find(parent => String(parent.id) === parentId)?.name;
  const selectedParent = parentTopics.find(parent => String(parent.id) === parentFilter);

  const fetchSubTopics = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const parentQuery = parentFilter ? `parentId.equals=${parentFilter}` : 'parentId.specified=true';
      const result = await axios.get<ITopic[]>(
        `api/topics?${parentQuery}&page=${activePage - 1}&size=${PAGE_SIZE}&sort=name,asc&cacheBuster=${new Date().getTime()}`
      );
      // a sub topic always has a parent, so a backend that ignores the parent filter can never list main topics here
      setSubTopics(Array.isArray(result.data) ? result.data.filter(topic => topic.parentId != null) : []);
      setTotalItems(parseInt(result.headers?.['x-total-count'], 10) || 0);
    } catch (e) {
      setSubTopics([]);
      setTotalItems(0);
      setLoadError(true);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    fetchSubTopics();
  }, [activePage, parentFilter]);

  // the confirmation message goes away by itself
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const parentName = (topic: ITopic) => parentTopics.find(parent => parent.id === topic.parentId)?.name ?? '-';

  const openModal = (topic: ITopic | null) => {
    setEditing(topic);
    setName(topic?.name ?? '');
    setParentId(topic?.parentId ? String(topic.parentId) : parentFilter);
    setParentKnown(!!topic?.parentId || !!parentFilter);
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) setModalOpen(false);
  };

  const save = async () => {
    if (!parentId) {
      setError('Please choose the parent topic');
      return;
    }
    if (!name.trim()) {
      setError('Please enter the sub topic name');
      return;
    }
    setSaving(true);
    try {
      const entity = { ...editing, name: name.trim(), parentId: Number(parentId) };
      if (editing) {
        await axios.put(`api/topics/${editing.id}`, entity);
      } else {
        await axios.post('api/topics', entity);
      }
      setModalOpen(false);
      const savedParent = parentTopics.find(parent => parent.id === entity.parentId)?.name;
      setNotice(`"${entity.name}" was ${editing ? 'updated' : 'added'}${savedParent ? ` under "${savedParent}"` : ''}`);
      await fetchSubTopics();
    } catch (e) {
      setError('An error occurred while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await axios.delete(`api/topics/${toDelete.id}`);
      setNotice(`"${toDelete.name}" was deleted`);
      setToDelete(null);
      // step back a page when the last item of the page was deleted
      if (subTopics.length === 1 && activePage > 1) {
        setActivePage(activePage - 1);
      } else {
        await fetchSubTopics();
      }
    } catch (e) {
      setDeleteError('This sub topic could not be deleted. It may be in use. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const emptyMessage = selectedParent ? `No sub topics under "${selectedParent.name}" yet.` : 'No sub topics found.';

  return (
    <div className="sub-topics">
      {selectedParent && onBackToTopics && (
        <Button color="link" className="px-0 mb-2" onClick={onBackToTopics} data-cy="backToTopicsButton">
          <FontAwesomeIcon icon="arrow-left" /> Back to Topics
        </Button>
      )}
      {selectedParent && (
        <h3 className="mb-3" data-cy="subTopicsHeading">
          Sub topics of <strong>{selectedParent.name}</strong>
        </h3>
      )}

      <Row className="topic-filter align-items-end">
        <Col md="4">
          <Label for="subtopic-parent-filter">Filter by Parent Topic</Label>
          <Input
            id="subtopic-parent-filter"
            type="select"
            value={parentFilter}
            onChange={e => {
              setParentFilter(e.target.value);
              setActivePage(1);
            }}
          >
            <option value="">All parent topics</option>
            {parentTopics.map(parent => (
              <option value={parent.id} key={parent.id}>
                {parent.name}
              </option>
            ))}
          </Input>
        </Col>
        <Col md="8" className="d-flex justify-content-md-end mt-3 mt-md-0">
          <Button color="primary" className="topic-small-btn" onClick={() => openModal(null)} data-cy="addSubTopicButton">
            <FontAwesomeIcon icon="plus" /> {selectedParent ? `Add Sub Topic to ${selectedParent.name}` : 'Add new Sub Topic'}
          </Button>
        </Col>
      </Row>

      {notice && (
        <Alert color="success" className="py-2" data-cy="subTopicNotice" toggle={() => setNotice('')}>
          {notice}
        </Alert>
      )}

      <div className={`loader-area table-responsive ${loading && hasLoaded ? 'is-refreshing' : ''}`}>
        {!hasLoaded ? (
          <Spinner className="body-loader-icon" />
        ) : loadError ? (
          <div className="text-center py-5" data-cy="subTopicLoadError">
            <p className="text-danger">Sub topics could not be loaded.</p>
            <Button color="primary" outline onClick={() => void fetchSubTopics()}>
              Try again
            </Button>
          </div>
        ) : subTopics.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th>Sub Topic</th>
                {!selectedParent && <th className="d-none d-md-table-cell">Parent Topic</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {subTopics.map(topic => (
                <tr key={topic.id}>
                  <td className="topic-name-cell">
                    {topic.name}
                    {/* small screens have no parent column, so the parent goes under the name */}
                    {!selectedParent && <div className="d-md-none small text-muted">{parentName(topic)}</div>}
                  </td>
                  {!selectedParent && <td className="d-none d-md-table-cell">{parentName(topic)}</td>}
                  <td className="actions-cell text-end">
                    <div className="icon-actions">
                      <IconActionButton
                        id={`subtopic-edit-${topic.id}`}
                        icon="pencil-alt"
                        label="Edit"
                        color="primary"
                        onClick={() => openModal(topic)}
                        dataCy="entityEditButton"
                      />
                      <IconActionButton
                        id={`subtopic-delete-${topic.id}`}
                        icon="trash"
                        label="Delete"
                        color="danger"
                        onClick={() => {
                          setDeleteError('');
                          setToDelete(topic);
                        }}
                        dataCy="entityDeleteButton"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="no-results text-center py-5" data-cy="subTopicEmpty">
            <p>{emptyMessage}</p>
            <Button color="primary" outline onClick={() => openModal(null)}>
              <FontAwesomeIcon icon="plus" /> Add the first one
            </Button>
          </div>
        )}
      </div>

      {totalItems > PAGE_SIZE && (
        <div>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={activePage} total={totalItems} itemsPerPage={PAGE_SIZE} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={activePage}
              onSelect={setActivePage}
              maxButtons={5}
              itemsPerPage={PAGE_SIZE}
              totalItems={totalItems}
            />
          </div>
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        toggle={closeModal}
        centered
        className="sub-topic-modal"
        // the parent is already known when adding from a parent's list, so go straight to the name
        onOpened={() => document.getElementById(parentId ? 'subtopic-name' : 'subtopic-parent')?.focus()}
      >
        <ModalHeader toggle={closeModal}>
          {editing ? 'Edit Sub Topic' : 'Add Sub Topic'}
          {parentKnown && modalParentName && (
            <>
              {editing ? ' of ' : ' to '}
              <strong>{modalParentName}</strong>
            </>
          )}
        </ModalHeader>
        <ModalBody>
          {!parentKnown && (
            <div className="mb-3">
              <Label for="subtopic-parent">
                Parent Topic <span className="text-danger">*</span>
              </Label>
              <Input
                id="subtopic-parent"
                type="select"
                value={parentId}
                onChange={e => {
                  setParentId(e.target.value);
                  setError('');
                }}
              >
                <option value="">Select parent topic...</option>
                {parentTopics.map(parent => (
                  <option value={parent.id} key={parent.id}>
                    {parent.name}
                  </option>
                ))}
              </Input>
            </div>
          )}
          <div className="mb-2">
            <Label for="subtopic-name">
              Sub Topic Name <span className="text-danger">*</span>
            </Label>
            <Input
              id="subtopic-name"
              type="text"
              value={name}
              maxLength={255}
              onChange={e => {
                setName(e.target.value);
                setError('');
              }}
              onKeyDown={e => e.key === 'Enter' && void save()}
              placeholder="e.g. Affordable senior housing"
            />
          </div>
          {error && (
            <div className="text-danger small" role="alert">
              {error}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" onClick={() => void save()} disabled={saving} data-cy="saveSubTopicButton">
            {saving ? <Spinner size="sm" /> : <FontAwesomeIcon icon="save" />} Save
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!toDelete} toggle={() => !deleting && setToDelete(null)} centered className="sub-topic-modal">
        <ModalHeader toggle={() => !deleting && setToDelete(null)}>Delete Sub Topic</ModalHeader>
        <ModalBody>
          <p className="mb-0">
            Delete the sub topic <strong>{toDelete?.name}</strong>? This cannot be undone.
          </p>
          {deleteError && (
            <div className="text-danger small mt-2" role="alert">
              {deleteError}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setToDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="danger" onClick={() => void confirmDelete()} disabled={deleting} data-cy="confirmDeleteButton">
            {deleting ? <Spinner size="sm" /> : <FontAwesomeIcon icon="trash" />} Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SubTopics;
